import { useState } from "react";
import {
  ArrowBigDown,
  ArrowBigUp,
  Check,
  CircleHelp,
  MessagesSquare,
  Pencil,
  Reply,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export const ago = (iso) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  return new Date(t).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const KindBadge = ({ kind }) => {
  const Icon = kind === "discussion" ? MessagesSquare : CircleHelp;
  return (
    <span className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      <Icon className="h-3 w-3" />
      {kind === "discussion" ? "Discussion" : "Question"}
    </span>
  );
};

export const VoteArrows = ({ votes, my, onVote, testId }) => {
  const tap = (value) => (e) => {
    e.stopPropagation();
    e.preventDefault();
    onVote(value);
  };
  return (
    <div className="flex w-10 shrink-0 flex-col items-center" data-testid={testId}>
      <button
        type="button"
        title="This is useful"
        data-testid={`${testId}-up`}
        onClick={tap(1)}
        className={
          my === 1
            ? "text-primary transition-transform duration-150 hover:scale-125"
            : "text-muted-foreground transition-transform duration-150 hover:scale-125 hover:text-foreground"
        }
      >
        <ArrowBigUp className="h-5 w-5" strokeWidth={my === 1 ? 2.6 : 1.7} />
      </button>
      <span className="font-data text-sm font-bold tabular-nums" data-testid={`${testId}-count`}>
        {votes}
      </span>
      <button
        type="button"
        title="This is not useful"
        data-testid={`${testId}-down`}
        onClick={tap(-1)}
        className={
          my === -1
            ? "text-[hsl(var(--at-risk))] transition-transform duration-150 hover:scale-125"
            : "text-muted-foreground transition-transform duration-150 hover:scale-125 hover:text-foreground"
        }
      >
        <ArrowBigDown className="h-5 w-5" strokeWidth={my === -1 ? 2.6 : 1.7} />
      </button>
    </div>
  );
};

export const Box = ({ value, onChange, placeholder, testId, rows = 3 }) => (
  <textarea
    data-testid={testId}
    rows={rows}
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full resize-y border border-border bg-background px-3 py-2 text-sm outline-none transition-colors duration-200 focus:border-primary"
  />
);

export const Author = ({ post }) => (
  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
    <span className="font-semibold text-foreground">{post.author || "—"}</span>
    {post.author_plant ? <span>· {post.author_plant}</span> : null}
    <span>· {ago(post.created_at)}</span>
    {post.edited_at ? <span>· edited</span> : null}
  </span>
);

const AcceptedChip = ({ id }) => (
  <span
    className="inline-flex items-center gap-1 border border-[hsl(var(--healthy))] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--healthy))]"
    data-testid={`accepted-${id}`}
  >
    <Check className="h-3 w-3" /> This settled it
  </span>
);

const Action = ({ label, icon: Icon, onClick, testId, tone }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors duration-200 ${
      tone || "text-muted-foreground hover:text-foreground"
    }`}
  >
    <Icon className="h-3.5 w-3.5" /> {label}
  </button>
);

/** One post on a thread. The tree is flattened by the caller, so nothing here recurses. */
export const ForumComment = ({ post, canAccept, canReply, onVote, onPost, onAccept, onEdit, onRemove }) => {
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [body, setBody] = useState(post.body);

  const indent = Math.min(post.depth || 0, 6) * 24;

  return (
    <div style={{ marginLeft: `${indent}px` }} data-testid={`forum-comment-${post.id}`}>
      <div
        className={`border border-border bg-card p-3 ${
          post.accepted ? "border-l-2 border-l-[hsl(var(--healthy))]" : ""
        }`}
      >
        <div className="flex items-start gap-3">
          <VoteArrows
            votes={post.votes}
            my={post.my_vote}
            testId={`vote-comment-${post.id}`}
            onVote={(v) => onVote(post, v)}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Author post={post} />
              {post.accepted ? <AcceptedChip id={post.id} /> : null}
            </div>

            {editing ? (
              <div className="mt-2">
                <Box
                  value={body}
                  onChange={setBody}
                  testId={`edit-comment-box-${post.id}`}
                  placeholder="Correct your post…"
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    className="rounded-none"
                    data-testid={`save-comment-${post.id}`}
                    onClick={() => onEdit(post, body).then((ok) => ok && setEditing(false))}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    onClick={() => {
                      setBody(post.body);
                      setEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p
                className={`mt-2 whitespace-pre-wrap text-sm ${
                  post.removed ? "italic text-muted-foreground" : ""
                }`}
              >
                {post.body}
              </p>
            )}

            {!post.removed && !editing ? (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px]">
                {canReply ? (
                  <Action
                    label="Reply"
                    icon={Reply}
                    testId={`reply-to-${post.id}`}
                    onClick={() => setReplying((v) => !v)}
                  />
                ) : null}
                {canAccept ? (
                  <Action
                    label={post.accepted ? "Unmark the answer" : "Mark as the answer"}
                    icon={Check}
                    testId={`accept-${post.id}`}
                    onClick={() => onAccept(post)}
                    tone={
                      post.accepted
                        ? "text-[hsl(var(--healthy))]"
                        : "text-muted-foreground hover:text-[hsl(var(--healthy))]"
                    }
                  />
                ) : null}
                {post.can_edit ? (
                  <Action
                    label="Edit"
                    icon={Pencil}
                    testId={`edit-comment-${post.id}`}
                    onClick={() => setEditing(true)}
                  />
                ) : null}
                {post.can_edit ? (
                  <Action
                    label="Remove"
                    icon={Trash2}
                    testId={`remove-comment-${post.id}`}
                    onClick={() => onRemove(post)}
                    tone="text-muted-foreground hover:text-[hsl(var(--at-risk))]"
                  />
                ) : null}
              </div>
            ) : null}

            {replying ? (
              <div className="mt-3 border-t border-border pt-3">
                <Box
                  value={draft}
                  onChange={setDraft}
                  testId={`reply-box-${post.id}`}
                  placeholder={`Reply to ${post.author}…`}
                />
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    className="rounded-none"
                    data-testid={`post-reply-${post.id}`}
                    onClick={() =>
                      onPost(draft, post.id).then((ok) => {
                        if (!ok) return;
                        setDraft("");
                        setReplying(false);
                      })
                    }
                  >
                    Post the reply
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-none"
                    onClick={() => setReplying(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
